export class Container {
    constructor() {
        this.bindings = {};
        this.resolved = {};
        this.resolving = [];
        this.dependents = new Map();
    }
    bindValue({ token, value, }) {
        const previousDeps = this.getDeps(this.bindings[token]);
        this.bindings[token] = {
            type: "value",
            deps: [],
            value,
        };
        this.updateDependents(token, previousDeps, []);
        this.invalidate(token);
    }
    bindFactory({ token, deps, factory, }) {
        const previousDeps = this.getDeps(this.bindings[token]);
        this.bindings[token] = {
            type: "factory",
            deps,
            factory: factory,
        };
        this.updateDependents(token, previousDeps, deps);
        this.invalidate(token);
    }
    /**
     * Retrieve a value from within the dependency injection container.
     * @param token
     * @returns
     */
    get({ token, }) {
        return this.resolveToken(token);
    }
    resolveAll() {
        const order = this.getResolutionOrder();
        order.forEach((token) => {
            this.resolveToken(token);
        });
    }
    resolveToken(token) {
        const cached = this.resolved[token];
        if (cached !== undefined) {
            return cached;
        }
        const binding = this.bindings[token];
        if (!binding) {
            throw new Error(`${token} was undefined in the DI container when retrieved.`);
        }
        if (this.isValueBinding(binding)) {
            this.resolved[token] = binding.value;
            return binding.value;
        }
        if (this.resolving.includes(token)) {
            const cycle = [...this.resolving, token].join(" -> ");
            throw new Error(`Dependency cycle detected: ${cycle}`);
        }
        this.resolving.push(token);
        try {
            if (!this.isFactoryBinding(binding)) {
                throw new Error(`Unknown dependency binding type for ${token}.`);
            }
            const deps = binding.deps.map((dep) => this.resolveToken(dep));
            const value = binding.factory(...deps);
            this.resolved[token] = value;
            this.resolving.pop();
            return value;
        }
        catch (error) {
            this.resolving.pop();
            throw error;
        }
    }
    getResolutionOrder() {
        var _a;
        const tokens = Object.keys(this.bindings);
        const inDegree = new Map();
        const dependents = new Map();
        const missingDeps = [];
        tokens.forEach((token) => {
            inDegree.set(token, 0);
            dependents.set(token, []);
        });
        tokens.forEach((token) => {
            const binding = this.bindings[token];
            const deps = this.getDeps(binding);
            deps.forEach((dep) => {
                var _a, _b;
                if (!this.bindings[dep]) {
                    missingDeps.push({ token, dep });
                    return;
                }
                inDegree.set(token, ((_a = inDegree.get(token)) !== null && _a !== void 0 ? _a : 0) + 1);
                (_b = dependents.get(dep)) === null || _b === void 0 ? void 0 : _b.push(token);
            });
        });
        if (missingDeps.length > 0) {
            const list = missingDeps
                .map(({ token, dep }) => `${token} -> ${dep}`)
                .join(", ");
            throw new Error(`Missing dependencies in DI container: ${list}`);
        }
        const queue = tokens.filter((token) => { var _a; return ((_a = inDegree.get(token)) !== null && _a !== void 0 ? _a : 0) === 0; });
        const order = [];
        while (queue.length > 0) {
            const token = queue.shift();
            order.push(token);
            (_a = dependents.get(token)) === null || _a === void 0 ? void 0 : _a.forEach((dependent) => {
                var _a;
                const next = ((_a = inDegree.get(dependent)) !== null && _a !== void 0 ? _a : 0) - 1;
                inDegree.set(dependent, next);
                if (next === 0) {
                    queue.push(dependent);
                }
            });
        }
        if (order.length !== tokens.length) {
            const cycle = this.findCycle(tokens);
            throw new Error(`Dependency cycle detected${cycle ? `: ${cycle}` : ""}`);
        }
        return order;
    }
    getDeps(binding) {
        if (!binding) {
            return [];
        }
        if (this.isFactoryBinding(binding)) {
            return binding.deps;
        }
        return [];
    }
    isFactoryBinding(binding) {
        return binding.type === "factory";
    }
    isValueBinding(binding) {
        return binding.type === "value";
    }
    findCycle(tokens) {
        const visited = new Set();
        const stack = new Set();
        const path = [];
        const visit = (token) => {
            if (stack.has(token)) {
                const start = path.indexOf(token);
                return [...path.slice(start), token].join(" -> ");
            }
            if (visited.has(token)) {
                return null;
            }
            visited.add(token);
            stack.add(token);
            path.push(token);
            const deps = this.getDeps(this.bindings[token]);
            for (let i = 0; i < deps.length; i += 1) {
                const dep = deps[i];
                if (this.bindings[dep]) {
                    const cycle = visit(dep);
                    if (cycle) {
                        return cycle;
                    }
                }
            }
            stack.delete(token);
            path.pop();
            return null;
        };
        for (let i = 0; i < tokens.length; i += 1) {
            const token = tokens[i];
            const cycle = visit(token);
            if (cycle) {
                return cycle;
            }
        }
        return null;
    }
    updateDependents(token, previousDeps, nextDeps) {
        previousDeps.forEach((dep) => {
            const dependents = this.dependents.get(dep);
            if (dependents) {
                dependents.delete(token);
                if (dependents.size === 0) {
                    this.dependents.delete(dep);
                }
            }
        });
        nextDeps.forEach((dep) => {
            const dependents = this.dependents.get(dep);
            if (dependents) {
                dependents.add(token);
            }
            else {
                this.dependents.set(dep, new Set([token]));
            }
        });
    }
    invalidate(token) {
        var _a, _b;
        if (this.resolved[token] === undefined &&
            ((_b = (_a = this.dependents.get(token)) === null || _a === void 0 ? void 0 : _a.size) !== null && _b !== void 0 ? _b : 0) === 0) {
            return;
        }
        const visited = new Set();
        const stack = [token];
        while (stack.length > 0) {
            const current = stack.pop();
            if (!visited.has(current)) {
                visited.add(current);
                if (this.resolved[current] !== undefined) {
                    delete this.resolved[current];
                }
                const dependents = this.dependents.get(current);
                if (dependents) {
                    dependents.forEach((dependent) => {
                        if (!visited.has(dependent)) {
                            stack.push(dependent);
                        }
                    });
                }
            }
        }
    }
}
export default Container;
//# sourceMappingURL=Container.js.map